import { useState, useEffect, useCallback } from "react";
import { Gift, Flame, Check, Lock } from "lucide-react";
import { useFb } from "../../utils/football";
import { useAuth } from "../../contexts/AuthContext";
import { hapticSuccess } from "../../utils/haptics";

/**
 * FootballDailyReward — football-only daily reward card (parallel to cricket's
 * DailyReward, fully independent streak). Asia/Dubai 24h reset handled by backend.
 */
export default function FootballDailyReward() {
  const fb = useFb();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [justCollected, setJustCollected] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    try { setStatus(await fb.get("/football/daily-reward/status")); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const claim = async () => {
    if (!user || collecting) return;
    setCollecting(true);
    try {
      const res = await fb.post("/football/daily-reward/claim");
      if (!res.already_collected_today) {
        setPointsEarned(res.points_earned);
        setJustCollected(true);
        hapticSuccess();
        setTimeout(() => setJustCollected(false), 3000);
      }
      fetchStatus();
    } catch { /* ignore */ } finally { setCollecting(false); }
  };

  if (!user || !status) return null;

  if (status.collected_today && !justCollected) {
    return (
      <div className="rounded-xl border border-border bg-card/60 p-4" data-testid="football-daily-reward-collected">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/20">
            <Check className="h-5 w-5 text-emerald-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold">Daily reward collected!</p>
            <p className="text-xs text-muted-foreground">Come back tomorrow · {status.streak}-day streak</p>
          </div>
          <div className="flex items-center gap-1 text-amber-400">
            <Flame className="h-4 w-4" /><span className="text-sm font-bold">{status.streak}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-900/30 to-orange-900/20" data-testid="football-daily-reward-available">
      {justCollected && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-emerald-500/10" data-testid="football-daily-reward-celebration">
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-400">+{pointsEarned} pts!</p>
            <p className="text-sm text-emerald-300">{status.streak}-day streak</p>
          </div>
        </div>
      )}
      <div className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-amber-400" />
            <h3 className="text-sm font-semibold">Daily Reward</h3>
          </div>
          {status.streak > 0 && (
            <div className="flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-amber-400">
              <Flame className="h-3 w-3" /><span className="text-xs font-bold">{status.streak}-day streak</span>
            </div>
          )}
        </div>

        <div className="mb-3 flex gap-1.5">
          {status.ladder?.map((d) => (
            <div
              key={d.day}
              className={`flex-1 rounded-lg py-1.5 text-center text-[10px] font-medium ${
                d.unlocked
                  ? "border border-emerald-500/40 bg-emerald-500/20 text-emerald-400"
                  : d.current
                  ? "border-2 border-amber-500/60 bg-amber-500/20 text-amber-300"
                  : "border border-slate-700/30 bg-slate-800/60 text-slate-500"
              }`}
              data-testid={`football-streak-day-${d.day}`}
            >
              {d.unlocked ? <Check className="mx-auto h-3.5 w-3.5" /> : d.current ? <Gift className="mx-auto h-3.5 w-3.5" /> : <Lock className="mx-auto h-3 w-3" />}
              <span className="mt-0.5 block">{d.points}</span>
            </div>
          ))}
        </div>

        <button
          onClick={claim}
          disabled={collecting || justCollected}
          data-testid="football-collect-daily-reward-btn"
          className="flex w-full items-center justify-center gap-2 rounded-xl wc-bg-gold py-2.5 text-sm font-extrabold text-slate-900 transition-all disabled:opacity-50"
        >
          {collecting ? "Collecting..." : <><Gift className="h-4 w-4" /> Collect +{status.points_if_collected} pts</>}
        </button>
      </div>
    </div>
  );
}
